import 'dayjs/locale/fr'

import { SUBSCRIPTION_ENTRY } from '@config'
import { validationSchema } from '@entries/contentful-subscription-form/validation'
import { yupResolver } from '@hookform/resolvers/yup'
import { useParameter } from '@hooks/use-parameter'
import { usePrivateDomainPath } from '@hooks/use-private-domain-path'
import { useTrackExternalLink } from '@hooks/use-track-external-link'
import { useTrackSubscription } from '@hooks/use-track-subscription'
import { useLocation } from '@reach/router'
import { Button } from '@ui/atoms/button'
import { Radio } from '@ui/atoms/radio'
import { TextField, type TextFieldProps } from '@ui/atoms/text-field/textfield'
import { EnhancedLink } from '@ui/enhanced-components/enhanced-components'
import {
  ENHANCED_COMPONENT_ROLE,
  ENHANCED_COMPONENT_TYPE,
} from '@ui/enhanced-components/types'
import { SupportedQueryParams } from '@utils/parameters'
import { isPlainObject } from '@utils/utils'
import queryString from 'query-string'
import React, { useCallback, useState } from 'react'
import { Controller, type SubmitHandler, useForm } from 'react-hook-form'

import { createSubscription, updateSubscription } from '../../../services/api'
import DialogError from './error-dialog'
import { AccountType, Civility, Country, type FormInput } from './types'

export const SubscriptionForm: React.FC<{
  content: Partial<Queries.ContentfulFormFragment>
}> = ({
  content: {
    contentful_id,
    titreFormulaire,
    codePromo,
    individualOnly,
    partnerName,
    vpDefaultSavings,
  },
}) => {
  const location = useLocation()
  const { memberID, origine } = queryString.parse(location.search)
  const origin = useParameter(SupportedQueryParams.Origin)
  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      accountType: individualOnly ? AccountType.INDIVIDUAL : undefined,
      birthDate: '',
      country: Country.DEFAULT,
    },
  })

  const contactUrl = usePrivateDomainPath('contact')
  const [errorCodes, setErrorCodes] = useState<Array<number | string>>([])
  const [errorPopinVisible, setErrorPopinVisible] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const trackExternalHyperlink = useTrackExternalLink()
  const trackSubscription = useTrackSubscription()

  const handleSubmitButtonClick = useCallback((e: React.SyntheticEvent) => {
    yupResolver(validationSchema)
    trackExternalHyperlink(e)
  }, [])

  const toggleErrorPopinVisibility = useCallback(() => {
    setErrorPopinVisible(!errorPopinVisible)
  }, [errorPopinVisible])

  const onBirthDateChange: TextFieldProps['onChange'] = (event) => {
    // @ts-expect-error
    let value = event.target.value.replace(/\D/g, '')
    if (value.length > 2) {
      value = value.slice(0, 2) + '/' + value.slice(2)
    }

    if (value.length > 5) {
      value = value.slice(0, 5) + '/' + value.slice(5)
    }

    if (value.length > 10) {
      value = value.slice(0, 10)
    }

    setValue('birthDate', value, { shouldValidate: true })
  }

  const onSubmit: SubmitHandler<FormInput> = useCallback(
    async (data) => {
      try {
        setHasSubmitted(true)
        const submittedData = {
          ...data,
          codePromo,
          from: location.href,
          codePub: origine as string,
          vpDefaultSavings,
          partnerMemberId: (memberID as string) || partnerName,
          partnerName: contentful_id,
        }

        const subscriptionResponse = await createSubscription(submittedData)
        const subscriptionResponseBody = await subscriptionResponse.json()
        const { id } = subscriptionResponseBody
        if (id) {
          await trackSubscription(id, submittedData.email)

          await updateSubscription(id)
          window.location.href = queryString.stringifyUrl({
            url: SUBSCRIPTION_ENTRY.COMPTE_BANCAIRE,
            query: {
              id,
              [SupportedQueryParams.Origin]: origin || undefined,
            },
          })
        }
      } catch (e: any) {
        const body = await e.json()
        const { data, status } = body
        if (isPlainObject(data) && data !== null) {
          const { statusCode, errorCodes } = data
          if (errorCodes?.length) {
            setErrorCodes(errorCodes)
          } else {
            setErrorCodes([`${statusCode}`])
          }
        } else if (status) {
          setErrorCodes([`${status}`])
        }

        setHasSubmitted(false)
        setErrorPopinVisible(true)
      }
    },
    [origin, origine, trackSubscription]
  )

  return (
    <div className="group overflow-hidden laptop:rounded-[24px] laptop:border laptop:border-solid laptop:border-grey-100 laptop:bg-theme-default-accent2 laptop:shadow-[0_20px_40px_-2px_rgb(35,35,35,0.08)]">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="bg-white-50/[0.80] px-2 laptop:p-12">
          {titreFormulaire && (
            <h2 className="group-hover:text-dark pb-10 text-150 leading-alternate">
              {titreFormulaire}
            </h2>
          )}
          <p className="group-hover:text-dark pb-1 text-085 leading-alternate">
            Type de compte
          </p>
          <Controller
            control={control}
            name="accountType"
            render={({ field }) => (
              <div className="grid grid-cols-2 gap-3">
                <Radio
                  inputProps={{ ...register('accountType') }}
                  label="Individuel"
                  selected={field.value === AccountType.INDIVIDUAL}
                  value={AccountType.INDIVIDUAL}
                />
                <Radio
                  disabled={Boolean(individualOnly)}
                  inputProps={{ ...register('accountType') }}
                  label="Joint"
                  selected={field.value === AccountType.JOINT}
                  value={AccountType.JOINT}
                />
              </div>
            )}
          />
          <p className="group-hover:text-dark pb-1 pt-5 text-085 leading-alternate">
            Civilité
          </p>
          <Controller
            control={control}
            name="civility"
            render={({ field }) => (
              <div className="grid grid-cols-2 gap-5">
                <Radio
                  inputProps={{ ...register('civility') }}
                  label="Madame"
                  selected={field.value === Civility.MADAME}
                  value={Civility.MADAME}
                />
                <Radio
                  inputProps={{ ...register('civility') }}
                  label="Monsieur"
                  selected={field.value === Civility.MONSIEUR}
                  value={Civility.MONSIEUR}
                />
              </div>
            )}
          />
          <div className="mt-5 grid grid-cols-1 gap-5 laptop:grid-cols-2">
            <div>
              <Controller
                control={control}
                name="firstName"
                render={({ field }) => (
                  <label className="text-085" htmlFor="firstName">
                    Prénom*
                    <TextField
                      fullWidth
                      className="mt-1 !min-w-0"
                      helper={errors.firstName?.message}
                      id="firstName"
                      placeholder="Entrer votre prénom..."
                      size="small"
                      type="text"
                      validation={errors.firstName ? 'error' : undefined}
                      {...field}
                    />
                  </label>
                )}
              />
            </div>
            <div>
              <Controller
                control={control}
                name="lastName"
                render={({ field }) => (
                  <label className="text-085" htmlFor="lastName">
                    Nom*
                    <TextField
                      fullWidth
                      className="mt-1 !min-w-0"
                      helper={errors.lastName?.message}
                      id="lastName"
                      placeholder="Entrer votre nom..."
                      size="small"
                      type="text"
                      validation={errors.lastName ? 'error' : undefined}
                      {...field}
                    />
                  </label>
                )}
              />
            </div>
            <div>
              <Controller
                control={control}
                name="birthDate"
                render={({ field }) => (
                  <label className="text-085" htmlFor="birthDate">
                    Date de naissance*
                    <TextField
                      fullWidth
                      className="mt-1 !min-w-0"
                      helper={errors.birthDate?.message}
                      id="birthDate"
                      placeholder="jj/mm/aaaa"
                      size="small"
                      type="tel"
                      validation={errors.birthDate ? 'error' : undefined}
                      {...field}
                      onChange={onBirthDateChange}
                    />
                  </label>
                )}
              />
            </div>
            <div>
              <Controller
                control={control}
                name="postalCode"
                render={({ field }) => (
                  <label className="text-085" htmlFor="postalCode">
                    Code postal*
                    <TextField
                      fullWidth
                      className="mt-1 !min-w-0"
                      helper={errors.postalCode?.message}
                      id="postalCode"
                      placeholder="Entrer votre code postal..."
                      size="small"
                      type="number"
                      validation={errors.postalCode ? 'error' : undefined}
                      {...field}
                    />
                  </label>
                )}
              />
            </div>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-5">
            <div>
              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <label htmlFor="email">
                    Email *
                    <TextField
                      fullWidth
                      className="mt-1 !min-w-0"
                      helper={errors.email?.message}
                      id="email"
                      label="Email *"
                      placeholder="Entrer votre email..."
                      size="small"
                      type="email"
                      validation={errors.email ? 'error' : undefined}
                      {...field}
                    />
                  </label>
                )}
              />
            </div>
            <div>
              <Controller
                control={control}
                name="tel"
                render={({ field }) => (
                  <label htmlFor="tel">
                    Téléphone portable *
                    <TextField
                      fullWidth
                      className="mt-1 !min-w-0"
                      helper={errors.tel?.message}
                      label="Téléphone portable *"
                      placeholder="Entrer votre numéro..."
                      size="small"
                      type="tel"
                      validation={errors.tel ? 'error' : undefined}
                      {...field}
                    />
                  </label>
                )}
              />
            </div>
          </div>
        </div>
        <div className="bg-white-50 pt-12 laptop:p-12">
          <div className="flex justify-center pt-1">
            <Button
              data-role={ENHANCED_COMPONENT_ROLE.SUBSCRIPTION_FORM}
              data-type={ENHANCED_COMPONENT_TYPE.CTA}
              disabled={!isValid || hasSubmitted}
              id={ENHANCED_COMPONENT_ROLE.SUBSCRIPTION_FORM}
              type="submit"
              onClick={handleSubmitButtonClick}
            >
              Ouvrir un compte
            </Button>
          </div>
          <p className="mt-3 text-075 leading-alternate text-[#6A6C72]">
            Les informations recueillies sur ce formulaire sont nécessaires à
            Fortuneo et ses prestataires pour permettre la création de votre
            dossier de souscription et le suivi de votre demande d'ouverture de
            compte. Conformément à la réglementation sur les données
            personnelles vous pouvez exercer à tout moment votre droit d'accès,
            de rectification et d'opposition pour un motif légitime au
            traitement de ces données à l'aide du numéro de dossier qui vous
            sera envoyé par e-mail,{' '}
            <EnhancedLink href={contactUrl}>en nous contactant</EnhancedLink>.
          </p>
          <DialogError
            codes={errorCodes}
            visible={errorPopinVisible}
            onClose={toggleErrorPopinVisibility}
          />
        </div>
      </form>
    </div>
  )
}
